import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { Button } from '@/kit';
import { meApi } from '../../api/endpoints/me';
import { useAuthStore } from '../../stores/authStore';
import { handleApiError } from '../../api/errorMapping';
import { SecurityPageSection } from '../layouts/SecurityPageSection';
import { LoadingButton } from '../ui/LoadingButton';

/**
 * Activación de SMS como 2º factor (DEC-RBAC-084). Análogo al switch a EMAIL, PERO sin pasos de
 * verificación: elegir SMS llama `POST /mfa/choose-sms` (sin OTP previo — el backend NO re-verifica,
 * igual que `choose-email`). Alcanzable desde /me/security cuando la security policy del tenant y el
 * teléfono del user habilitan SMS (el backend lo lista en `allowedMethods`).
 *
 * Si el user no tiene teléfono, el backend rechaza `choose-sms` (el error se muestra vía handleApiError).
 */
export function MfaSmsWizard() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const refresh = useAuthStore((s) => s.refresh);

    const chooseMut = useMutation({
        mutationFn: () => meApi.chooseSmsMfa(),
        onSuccess: async () => {
            toast.success('Código por SMS activado');
            // Rehidratar el perfil + invalidar el estado MFA para que /me refleje el nuevo 2º factor.
            await refresh();
            await qc.invalidateQueries({ queryKey: ['me', 'security', 'mfa'] });
            void navigate({ to: '/me/profile' });
        },
        onError: handleApiError,
    });

    return (
        <SecurityPageSection title="Verificación por SMS" centered>
            <p className="max-w-md text-sm text-muted-foreground">
                Vas a recibir un código por SMS en tu teléfono cada vez que inicies sesión. No hace
                falta verificar nada ahora: se activa al instante.
            </p>
            <div className="max-w-md space-y-4">
                <LoadingButton
                    className="w-full"
                    isLoading={chooseMut.isPending}
                    loadingText="Activando…"
                    onClick={() => {
                        chooseMut.mutate();
                    }}
                >
                    Usar SMS como segundo factor
                </LoadingButton>
                <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                        void navigate({ to: '/me/profile' });
                    }}
                >
                    Cancelar
                </Button>
            </div>
        </SecurityPageSection>
    );
}
